interface Coordinates {
  latitude: number;
  longitude: number;
}

/**
 * Fetch coordinates (latitude, longitude) for a given postal code and house number
 * Uses the same PDOK API as used in AddressStep component
 * @param postCode The postal code to search for
 * @param houseNumber Optional house number to improve accuracy
 * @returns Promise resolving to coordinates or null if not found
 */
export async function fetchCoordinatesFromPostalCode(
  postCode: string,
  houseNumber?: string
): Promise<Coordinates | null> {
  if (!postCode || postCode.length < 6) {
    return null;
  }

  // Get the API URL from environment variable
  const apiUrl = process.env.NEXT_PUBLIC_PDOK_API;
  
  if (!apiUrl) {
    console.error('NEXT_PUBLIC_PDOK_API environment variable is not set');
    return null;
  }

  try {
    // Clean up postal code (remove spaces)
    const cleanPostCode = postCode.replace(/\s+/g, '');
    
    // Build query - include house number if provided, otherwise just postal code
    const query = houseNumber 
      ? `${cleanPostCode}+${houseNumber}`
      : cleanPostCode;
    
    const fullUrl = `${apiUrl}?q=${query}&fq=type:adres&rows=1`;
    const response = await fetch(fullUrl);

    if (!response.ok) {
      console.error(`PDOK API HTTP error: ${response.status} - ${response.statusText}`);
      return null;
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.error('PDOK API did not return JSON. Content-Type:', contentType);
      return null;
    }

    const data = await response.json();
    const apiAddress = data.response?.docs?.[0];

    if (apiAddress) {      
      // Check for coordinates in centroide_ll field
      if (apiAddress.centroide_ll) {
        // Handle POINT format: "POINT(longitude latitude)"
        if (typeof apiAddress.centroide_ll === 'string' && apiAddress.centroide_ll.startsWith('POINT(')) {
          const pointMatch = apiAddress.centroide_ll.match(/POINT\(([\d\.-]+)\s+([\d\.-]+)\)/);
          if (pointMatch) {
            const longitude = Number(pointMatch[1]);
            const latitude = Number(pointMatch[2]);
            
            if (!isNaN(latitude) && !isNaN(longitude)) {
              return { latitude, longitude };
            }
          }
        }
        
        // Handle comma-separated format: "longitude,latitude" (fallback)
        if (typeof apiAddress.centroide_ll === 'string' && apiAddress.centroide_ll.includes(',')) {
          const [longitude, latitude] = apiAddress.centroide_ll.split(',').map(Number);
          
          if (!isNaN(latitude) && !isNaN(longitude)) {
            return { latitude, longitude };
          }
        }
      }
      
      // Alternative: check for coordinates in different fields
      if (apiAddress.lat && apiAddress.lon) {
        const latitude = Number(apiAddress.lat);
        const longitude = Number(apiAddress.lon);
        
        if (!isNaN(latitude) && !isNaN(longitude)) {
          return { latitude, longitude };
        }
      }
    }

    return null;
  } catch (error) {
    console.error('Error fetching coordinates from PDOK API:', error);
    return null;
  }
}

/**
 * Fetch coordinates for a school based on its postal code and address
 * @param school The school object containing postal code and address info
 * @returns Promise resolving to coordinates or null if not found
 */
export async function fetchSchoolCoordinates(school: {
  postcode?: string;
  huisnummer_toevoeging?: string;
}): Promise<Coordinates | null> {
  if (!school.postcode) {
    return null;
  }
  
  return fetchCoordinatesFromPostalCode(
    school.postcode,
    school.huisnummer_toevoeging || undefined
  );
} 

/**
 * Parses a "POINT(longitude latitude)" string into a coordinates object.
 * Returns null if the format is invalid.
 * @param pointString - The coordinate string, e.g., "POINT(5.11985472 52.09072644)"
 */
export const parsePointCoordinates = (
  pointString: string | undefined | null,
): { latitude: number; longitude: number } | null => {
  if (!pointString || typeof pointString !== 'string' || !pointString.startsWith('POINT(')) {
    return null;
  }

  const pointMatch = pointString.match(/POINT\(([\d.-]+)\s+([\d.-]+)\)/);

  if (pointMatch && pointMatch.length === 3) {
    const longitude = Number(pointMatch[1]);
    const latitude = Number(pointMatch[2]);

    if (!isNaN(latitude) && !isNaN(longitude)) {
      return { latitude, longitude };
    }
  }

  // Return null if the regex fails or the numbers are invalid
  return null;
};